import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Router } from '@angular/router';
import {
	BehaviorSubject,
	catchError,
	Observable,
	of,
	shareReplay,
	Subject,
	tap,
} from 'rxjs';
import { JWT } from 'src/app/global/models/jwt';
import { User } from 'src/app/global/models/user';
import { APIService } from 'src/app/global/services/api.service';
import { UserRegistration } from '../models/user-registration-model';
import { UserLogin } from '../models/user-login-model';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { UserResetPassword } from '../models/user-reset-password';
import { Tools } from 'src/utilities/tools';
import { environment } from 'src/environments/environment';
import { Buffer } from 'buffer';
import { MatDialog } from '@angular/material/dialog';
import { CustomBrandingService } from '../../user/custom-branding/services/custom-branding.service';
import { map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class AccountService {
	ACCOUNT_API_V2 = environment.ACCOUNT_API_V2;
	isLoggedIn: BehaviorSubject<Boolean> = new BehaviorSubject<Boolean>(false);
	public loggedInUpdate = this.isLoggedIn.asObservable();

	private shareFundraiserEmbedSubject: Subject<String> = new Subject<String>();
	getWidgetCheckbox$ = this.shareFundraiserEmbedSubject.asObservable();

	/*
	 * DESCRIPTION: This class depends upon below PARAM
	 * PARAM: _apiService
	 * PARAM: _router
	 */
	constructor(
		private _apiService: APIService,
		private _router: Router,
		private notificationService: NotificationService,
		public HttpClient: HttpClient,
		@Inject(LOCALE_ID) public locale: string,
		private dialogRef: MatDialog
	) {}

	/**
	 * Function to get the profile ID
	 * @returns Observable<any>
	 */
	getProfileId(): Observable<any> {
		const headers = this.getHeaders();
		const url = `${environment.ACCOUNT_API_V2}account/profile/`;

		return this.HttpClient.get(url, { headers }).pipe(
			map((response: any) => {
				// Process the response as needed (e.g., extract data, handle errors).
				// For now, let's just return the profile ID directly.
				return response;
			}),
			catchError((error) => {
				// Handle any errors (e.g., log, show a user-friendly message).
				console.error('Error fetching profile:', error);
				return of(0); // Return 0 in case of an error
			})
		);
	}

	/**
	 * Function to login
	 * @param userLoginDetails - The login details of the user
	 */
	login(userLoginDetails: UserLogin): void {
		const endPointUrl = `${this.ACCOUNT_API_V2}account/user/login/`;
		const body = userLoginDetails;

		this._apiService.tempPost(endPointUrl, body).subscribe(
			(response) => {
				this.handleLoginResponse(response, userLoginDetails.email);
			},
			(error) => {
				this.handleLoginError();
			}
		);
	}

	/**
	 * Handle the login response
	 * @param response - The response from the login API
	 * @param email - The email of the user
	 */
	private handleLoginResponse(response: any, email: string): void {
		if (response.errors?.hasOwnProperty('code')) {
			this.handleIncorrectCredentials();
		} else if (
			response.data.is_email_verified == 0 ||
			response.data.is_email_verified == '0'
		) {
			this.navigateToRegistrationComplete(email);
		} else if (response.data.is_active == 0 || response.data.is_active == '0') {
			this.handleAccountDeactivated();
		} else {
			this.processSuccessfulLogin(response, email);
		}
	}

	/**
	 * Handle incorrect credentials
	 */
	private handleIncorrectCredentials(): void {
		this.notificationService.openNotification(
			$localize`:@@account_login_incorrectCredientials_error:Error: Incorrect Email & Password`,
			'',
			'error'
		);
		this.setIsUserLoggedIn(false);
	}

	/**
	 * Navigate to the registration complete page
	 * @param email - The email of the user
	 */
	private navigateToRegistrationComplete(email: string): void {
		this._router.navigate(['account/registration-complete', { email }]);
	}

	/**
	 * Handle account deactivated
	 */
	private handleAccountDeactivated(): void {
		this.notificationService.openNotification(
			$localize`:@@account_login_account_deactivated_error:Error: Your account is deactivated please contact customer care to activate.`,
			'',
			'error'
		);
		this.isLoggedIn.next(false);
	}

	/**
	 * Process successful login
	 * @param response - The response from the login API
	 * @param email - The email of the user
	 */
	private processSuccessfulLogin(response: any, email: string): void {
		const user = new User(
			response.data.id,
			new JWT(response.data.jwt, ''),
			response.data.first_name,
			response.data.last_name,
			email,
			response.data.profile_image !== 'No image found'
				? response.data.profile_image
				: null,
			response.data.is_email_verified
		);

		// Save user info into local storage
		localStorage.setItem('user', JSON.stringify(user));
		// Save login time to local storage
		localStorage.setItem('lastLogin', new Date().getTime().toString());
		// Set user logging flag
		this.setIsUserLoggedIn(true);
		// Send notification
		this.notificationService.openNotification(
			$localize`:@@account_login_loginSuccessfully_notification:Login successfully`,
			'OK',
			'success'
		);

		// 🔹 Check if QR save was pending before login
		const savedPayload = sessionStorage.getItem('qrSavePayload');
		if (savedPayload) {
			// ✅  Redirect to the custom branding page where QR will be saved
			this._router.navigate(['/custom-branding/qrcode']);
		} else {
			// No pending QR save, navigate to previous path or dashboard
			const previousPath = Tools.getPreviousPath() || 'dashboard';
			const previousPathWithoutLocale = Tools.getPathnameWithoutLocale(
				previousPath,
				this.getLocaleId()
			);
			this._router.navigate([previousPathWithoutLocale]);
		}

		// Set user language
		Tools.setUserlanguage(this.getLocaleId());
	}

	/**
	 * Handle login error
	 */
	private handleLoginError(): void {
		this.notificationService.openNotification(
			$localize`:@@account_login_incorrectCredientials_error:Error: Incorrect Email & Password`,
			'',
			'error'
		);
		//this.setIsUserLoggedIn(false);
	}

	/*
	 * Function To set logout
	 * @param value
	 */
	logout() {
		localStorage.clear();

		if (
			!(this._router.url == '/') &&
			!(this._router.url == '/organisation') &&
			!(this._router.url == '/search')
		) {
			this._router.navigate(['/account']);
		}

		this.isLoggedIn.next(false);
	}

	/*
	 * Function To logout when JWT expires
	 * @param value
	 */
	autoLogout() {
		localStorage.clear();
		this.dialogRef.closeAll();
		if (
			!(this._router.url == '/') &&
			!(this._router.url == '/organisation') &&
			!(this._router.url == '/search')
		) {
			this.notificationService.openNotification(
				$localize`:@@account_login_youHavebeenLogout_notification:You have been logout of your account. Kindly login again.`,
				'',
				'error'
			);
			this._router.navigate(['/account']);
		}

		this.isLoggedIn.next(false);
	}

	/*
	 * Function To verify email
	 * @param value
	 */
	verifyEmail(data: any) {
		let endPoint = 'account/verify_email/';
		return this.HttpClient.post(this.ACCOUNT_API_V2 + endPoint, data);
	}

	/*
	 * Function to get current login status
	 */
	getLoginInformation() {
		//CHECK IF USER IS LOGGED IN
		if (this.isLoggedIn.getValue() == false) {
			//CHECK IF JWT/USER EXISTS
			let user = localStorage.getItem('user') || '';
			if (user != null && user != undefined && user.length > 0) {
				//CHECK IF JWT IS VALID
				if (!this.isJWTExpired()) {
					this.isLoggedIn.next(true);
				} else {
					//JWT is has expired --> logout
				}
			}
		}

		return this.isLoggedIn;
	}

	/*
	 *Function to check if JWT has expired
	 */

	isJWTExpired(): boolean {
		let token = '';
		let user = localStorage.getItem('user') || '{}';
		if (user != '{}' && user != null && user != undefined) {
			let jwt = JSON.parse(user)?.jwt?.jwt || null;
			if (jwt != null && jwt != undefined && jwt != '{}') {
				token = jwt;
			} else {
				console.error('Can not find JWT');
				return true;
			}
		}
		try {
			// Split the JWT token into parts
			const parts = token.split('.');
			if (parts.length !== 3) {
				console.error('Invalid token format');
				return true;
			}

			// Decode the payload part of the JWT token
			const base64Url = parts[1];
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map(function (c) {
						return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join('')
			);

			// Parse the payload as JSON and extract the exp field
			const { exp } = JSON.parse(jsonPayload);
			if (!exp) {
				throw new Error('Token does not contain exp field');
			}

			// Compare the exp field with the current timestamp
			const currentTimestamp = Math.floor(new Date().getTime() / 1000);
			if (currentTimestamp > exp) {
				return true;
			}
			return false;
		} catch (err) {
			// Log the error and return false
			console.error(err);
			return true;
		}
	}

	/*
	 * Function to check if an email already exists in whydonate database
	 */
	isEmailInUse(email: string) {
		let endPointUrl: string = 'account/user/verify_email/';
		let body = { email: email };
		return this.HttpClient.post(this.ACCOUNT_API_V2 + endPointUrl, body);
	}

	/*
	 * Function to register a user
	 */
	register(userDetails: UserRegistration) {
		// Extract Data and Make form data
		let registerFormData = new FormData();
		if (userDetails.image) {
			registerFormData.append(
				'image',
				Tools.base64toBlob(userDetails.image, userDetails.image_type) ||
					new Blob([]),
				userDetails.image_name
			);
		}
		registerFormData.append('name', userDetails.name);
		registerFormData.append(
			'language_code',
			userDetails.language_code ? userDetails.language_code : 'nl'
		);
		registerFormData.append('type', userDetails.type);
		registerFormData.append(
			'phone_number',
			userDetails.phone_number != undefined ? userDetails.phone_number : null
		);
		registerFormData.append('first_name', userDetails.first_name);
		registerFormData.append('last_name', userDetails.last_name);
		registerFormData.append('email', userDetails.email);
		registerFormData.append('password', userDetails.password);
		registerFormData.append('iv', userDetails.iv);
		registerFormData.append('previous_path', userDetails.previous_path);
		return this.HttpClient.post(
			this.ACCOUNT_API_V2 + 'account/user/',
			registerFormData,
			{ headers: new HttpHeaders().set('Origin', environment.domain) }
		);
	}

	/*
	 * Function to create a Merchant on OPP
	 */
	async createMerchant(payload: any) {
		let url = 'account/merchant';
		let headers = this.getHeaders();
		return await this.HttpClient.post(this.ACCOUNT_API_V2 + url, payload, {
			headers: headers,
		}).toPromise();
	}

	async getClientSecret() {
		const headers = this.getHeaders();
		let endPointUrl =
			environment.ACCOUNT_API_V2 + 'account/stripe/accountonboard';
		return await this.HttpClient.get(endPointUrl, {
			headers: headers,
		}).toPromise();
	}

	/*
	 * Function to resend verification email
	 */
	resendVerificationEmail(email: string) {
		let body = { email: email };
		return this.HttpClient.post(
			this.ACCOUNT_API_V2 + 'account/resend_email',
			body,
			{ headers: new HttpHeaders().set('Origin', environment.domain) }
		);
	}

	/*
	 * Function to get Locale Id
	 */
	getLocaleId(): string {
		if (this.locale.startsWith('en')) {
			return 'en';
		}
		if (this.locale.startsWith('es')) {
			return 'es';
		}
		if (this.locale.startsWith('de')) {
			return 'de';
		}
		if (this.locale.startsWith('fr')) {
			return 'fr';
		}
		if (this.locale.startsWith('bg')) {
			return 'bg';
		}
		if (this.locale.startsWith('hr')) {
			return 'hr';
		}
		if (this.locale.startsWith('cs')) {
			return 'cs';
		}
		if (this.locale.startsWith('da')) {
			return 'da';
		}
		if (this.locale.startsWith('fi')) {
			return 'fi';
		}
		if (this.locale.startsWith('el')) {
			return 'el';
		}
		if (this.locale.startsWith('hu')) {
			return 'hu';
		}
		if (this.locale.startsWith('it')) {
			return 'it';
		}
		if (this.locale.startsWith('pl')) {
			return 'pl';
		}
		if (this.locale.startsWith('pt')) {
			return 'pt';
		}
		if (this.locale.startsWith('ro')) {
			return 'ro';
		}
		if (this.locale.startsWith('sk')) {
			return 'sk';
		}
		if (this.locale.startsWith('sv')) {
			return 'sv';
		}
		if (this.locale.startsWith('uk')) {
			return 'uk';
		}
		return 'nl';
	}

	/*
	 * Function to set user is logged In
	 */
	setIsUserLoggedIn(value: Boolean) {
		this.isLoggedIn.next(value);
	}

	/*
	 * Function for forgot password
	 */
	forgotPassword(email: any) {
		let endPointUrl: string = 'account/user/forget_password/';
		let headers = this.getHeaders();
		return this.HttpClient.post(this.ACCOUNT_API_V2 + endPointUrl, email, {
			headers: headers,
		});
	}
	async encryptSymmetric(plaintext: string, key: string) {
		// create a random 96-bit initialization vector (IV)
		const iv = crypto.getRandomValues(new Uint8Array(12));

		// encoding the text
		const encodedPlaintext = new TextEncoder().encode(plaintext);

		// preparing the secret key for encryption
		const secretKey = await crypto.subtle.importKey(
			'raw',
			Buffer.from(key, 'base64'),
			{
				name: 'AES-GCM',
				length: 256,
			},
			true,
			['encrypt', 'decrypt']
		);

		// encrypting the text with the secret key
		const ciphertext = await crypto.subtle.encrypt(
			{
				name: 'AES-GCM',
				iv,
			},
			secretKey,
			encodedPlaintext
		);

		// returning the encrypted text "ciphertext" and the IV
		// encoded in base64
		return {
			ciphertext: Buffer.from(ciphertext).toString('base64'),
			iv: Buffer.from(iv).toString('base64'),
		};
	}

	async decryptSymmetric(ciphertext: string, iv: string, key: string) {
		// preparing the secret key
		const secretKey = await crypto.subtle.importKey(
			'raw',
			Buffer.from(key, 'base64'),
			{
				name: 'AES-GCM',
				length: 256,
			},
			true,
			['encrypt', 'decrypt']
		);

		// decrypting the encrypted text "ciphertext" with the secret key and IV
		const cleartext = await crypto.subtle.decrypt(
			{
				name: 'AES-GCM',
				iv: Buffer.from(iv, 'base64'),
			},
			secretKey,
			Buffer.from(ciphertext, 'base64')
		);

		// decoding the text and return it
		return new TextDecoder().decode(cleartext);
	}

	/**
	 * Function to check token validation
	 */
	isResetPasswordTokenValid(email: any, token: any) {
		let endPointUrl: string = 'account/user/reset_password_token_validate/';
		// return this._apiService.post(endPointUrl, { user_id, token });
		let headers = this.getHeaders();
		return this.HttpClient.post(
			this.ACCOUNT_API_V2 + endPointUrl,
			{ email, token },
			{
				headers: headers,
			}
		);
	}

	/*
	 * Function to register a user
	 */
	resetPassword(reset_password: UserResetPassword) {
		let endPointUrl: string = 'account/user/reset_password/';
		let body = reset_password;
		let headers = this.getHeaders();
		return this.HttpClient.post(this.ACCOUNT_API_V2 + endPointUrl, body, {
			headers: headers,
		});
	}

	getProfile() {
		let profileAPI: string = 'account/profile';
		let headers = this.getHeaders();
		return this.HttpClient.get(this.ACCOUNT_API_V2 + profileAPI, {
			headers: headers,
		});
	}

	/**
	 * Function to get HttpHeader authorization header after session started
	 * @returns HttpHeaders
	 */
	// getHeaders() {
	// 	let user = localStorage.getItem('user') || '{}';
	// 	if (user != '{}' && user != null && user != undefined) {
	// 		let jwt = JSON.parse(user)?.jwt?.jwt || null;
	// 		if (jwt != null && jwt != undefined && jwt != '{}') {
	// 			return new HttpHeaders()
	// 				.set('content-type', 'application/json')

	// 				.set('Access-Control-Allow-Origin', '*') //! throws cors error */
	// 				.set('Authorization', `JWT ${jwt}`);
	// 		} else {
	// 			return new HttpHeaders().set('content-type', 'application/json');
	// 		}
	// 	} else {
	// 		return new HttpHeaders().set('content-type', 'application/json');
	// 	}
	// }
	getHeaders(isFormData = false) {
		let user = localStorage.getItem('user') || '{}';
		if (user !== '{}' && user != null && user !== undefined) {
			let jwt = JSON.parse(user)?.jwt?.jwt || null;
			if (jwt != null && jwt !== undefined && jwt !== '{}') {
				let headers = new HttpHeaders().set('Authorization', `JWT ${jwt}`);

				if (!isFormData) {
					headers = headers.set('Content-Type', 'application/json');
				}

				return headers;
			}
		}
		return new HttpHeaders().set('Content-Type', 'application/json');
	}

	checkHeaders() {
		let user = localStorage.getItem('user') || '{}';
		if (user != '{}' && user != null && user != undefined) {
			let jwt = JSON.parse(user)?.jwt?.jwt || null;
			if (jwt != null && jwt != undefined && jwt != '{}' && jwt.length != 0) {
				return true;
			} else {
				return false;
			}
		} else {
			return false;
		}
	}

	/**
	 * Function to get Stripe country information based on the locale
	 */
	getStripeCountry(): Observable<any> {
		// Get the locale ID
		const locale = this.getLocaleId();

		// Construct the endpoint URL
		const endPoint = `account/stripe/countries?locale=en`;

		// Make the HTTP GET request to fetch Stripe country information
		return this.HttpClient.get(`${this.ACCOUNT_API_V2}${endPoint}`).pipe(
			catchError((error) => {
				// Handle any errors that occur during the HTTP request
				console.error('Error fetching Stripe country information:', error);
				// Return an observable with a user-friendly error message
				return of({ error: 'Failed to fetch Stripe country information' });
			})
		);
	}

	createStripeAccount(selectedCountry: any) {
		let endPoint = 'account/stripe/create';
		let headers = this.getHeaders();
		return this.HttpClient.post(
			this.ACCOUNT_API_V2 + endPoint,
			{
				country: selectedCountry,
			},
			{
				headers: headers,
			}
		);
	}

	getWidgetCheckbox() {
		return this.getWidgetCheckbox$;
	}

	setWidgetCheckbox(value: String) {
		this.shareFundraiserEmbedSubject.next(value);
	}
}
